import Decimal from 'decimal.js-light';
import { useMemo } from 'react';

import { shortFormatter } from '@/lib/formatting';

import { fontStyles } from '../../styles/typography';

type Props = {
  className?: string;
  number: number | Decimal;
  maxDigits?: number;
  symbol?: string;
  symbolPosition?: 'left' | 'right';
};

function shouldUseSubscriptNotation(number: Decimal, maxDigits: number): boolean {
  if (number.gte(new Decimal(1))) return false;

  return number.lte(new Decimal(1 / 10 ** maxDigits)) && !number.isZero();
}

function formatNonSubscriptNumber({
  number,
  maxDigits,
  symbol,
  symbolPosition,
}: {
  number: Decimal;
  maxDigits: number;
  symbol?: string;
  symbolPosition: 'left' | 'right';
}): string {
  if (number.gte(10 ** (maxDigits - 1))) {
    const compact = shortFormatter.format(number.toNumber());
    if (symbolPosition === 'left') {
      return `${symbol}${compact}`;
    }
    return `${compact}${symbol}`;
  }

  const integerPartLength = number.toFixed(0).length;
  const decimalDigits = Math.max(maxDigits - integerPartLength, 0);
  const formattedNumber = Number(number.toFixed(decimalDigits)).toLocaleString('en-US', {
    maximumFractionDigits: decimalDigits,
    minimumFractionDigits: decimalDigits,
  });

  if (symbolPosition === 'left') {
    return `${symbol}${formattedNumber}`;
  }

  return `${formattedNumber}${symbol}`;
}

function getSubscriptParts(number: Decimal): { subscriptSize: number; tail: string } {
  const numDecimals = number.decimalPlaces();
  let positionOfFirstNonZeroDigit = 1;
  const asString = number.toString().split('.')[1];
  for (let i = 0; i < numDecimals; i++) {
    if (asString[i] !== '0') {
      positionOfFirstNonZeroDigit = i;
      break;
    }
  }

  let tail = asString.slice(positionOfFirstNonZeroDigit);
  if (tail.length > 4) {
    tail = tail.slice(0, 4);
  }

  return {
    subscriptSize: positionOfFirstNonZeroDigit - 1,
    tail,
  };
}

export function SubscriptNotation({
  className,
  number,
  maxDigits = 7,
  symbol = '$',
  symbolPosition = 'left',
}: Props) {
  const formattedNumber = useMemo(() => {
    const decimalNumber = number instanceof Decimal ? number : new Decimal(number);
    if (shouldUseSubscriptNotation(decimalNumber, maxDigits)) {
      const { subscriptSize, tail } = getSubscriptParts(decimalNumber);
      return (
        <>
          {`${symbolPosition === 'left' ? symbol : ''}`}0.0
          <sub className="text-[11px]">{subscriptSize}</sub>
          {tail}
          {symbolPosition === 'right' ? symbol : ''}
        </>
      );
    }

    return formatNonSubscriptNumber({ number: decimalNumber, maxDigits, symbol, symbolPosition });
  }, [number, maxDigits, symbol, symbolPosition]);

  return (
    <span
      className={fontStyles({
        variant: 'BODY_SM',
        class: ['text-token-gray-800', 'leading-[18px]', className],
      })}
    >
      {formattedNumber}
    </span>
  );
}
